import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Divider,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableCell,
  TableRow,
  Grid,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Snackbar,
  Card,
  CardContent,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import QrCodeIcon from '@mui/icons-material/QrCode';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import api from '../config/api';
import DataTable from '../components/DataTable';

export default function TrainerPayments() {
  const [activeTab, setActiveTab] = useState(0); // 0: Payment Details, 1: Withdrawal Requests
  const [loading, setLoading] = useState(true);
  const [trainersData, setTrainersData] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [summary, setSummary] = useState({
    total_trainers: 0,
    configured_accounts: 0,
    total_pending_requests: 0,
    total_pending_amount: 0,
    total_completed_amount: 0,
  });

  // Filters for Tab 0: Trainer Payment Details
  const [searchTrainer, setSearchTrainer] = useState('');
  const [filterMethod, setFilterMethod] = useState('all');

  // Filters for Tab 1: Withdrawal Requests
  const [filterRequestStatus, setFilterRequestStatus] = useState('all');
  const [searchRequest, setSearchRequest] = useState('');

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [editFormData, setEditFormData] = useState({
    payout_method: 'bank',
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    upi_id: '',
  });

  // Action Dialogs for Requests
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [activeRequest, setActiveRequest] = useState(null);
  const [referenceId, setReferenceId] = useState('');
  const [adminRemarks, setAdminRemarks] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);

  // View Request Details dialog
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);

  // Toast / notification
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    showToast(`Copied ${label} to clipboard: ${text}`);
  };

  // Fetch Trainer Payment Details & Summaries
  const fetchTrainerPayments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTrainer) params.append('search', searchTrainer);
      if (filterMethod && filterMethod !== 'all') params.append('payout_method', filterMethod);

      const res = await api.get(`/wallet/admin/trainer-payments?${params.toString()}`);
      if (res.data?.success) {
        setTrainersData(res.data.data || []);
        if (res.data.summary) {
          setSummary(res.data.summary);
        }
      }
    } catch (err) {
      console.error('Error fetching trainer payments:', err);
      showToast(err.response?.data?.error || 'Failed to fetch trainer payment details', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Withdrawal Requests
  const fetchWithdrawalRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterRequestStatus && filterRequestStatus !== 'all') params.append('status', filterRequestStatus);
      if (searchRequest) params.append('search', searchRequest);

      const res = await api.get(`/wallet/admin/withdrawal-requests?${params.toString()}`);
      if (res.data?.success) {
        setWithdrawalRequests(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching withdrawal requests:', err);
      showToast(err.response?.data?.error || 'Failed to fetch withdrawal requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchTrainerPayments();
    } else {
      fetchWithdrawalRequests();
    }
  }, [activeTab, filterMethod, filterRequestStatus]);

  const handleOpenEdit = (trainer) => {
    setSelectedTrainer(trainer);
    setEditFormData({
      payout_method: trainer.payout_method || 'bank',
      account_holder_name: trainer.account_holder_name || '',
      bank_name: trainer.bank_name || '',
      account_number: trainer.account_number || '',
      ifsc_code: trainer.ifsc_code || '',
      upi_id: trainer.upi_id || '',
    });
    setEditDialogOpen(true);
  };

  const handleSavePaymentDetails = async () => {
    if (!selectedTrainer) return;
    try {
      setSubmittingAction(true);
      const payload = {
        trainer_id: selectedTrainer.trainer_id,
        ...editFormData,
      };
      const res = await api.post('/wallet/payment-details', payload);
      if (res.data?.success) {
        showToast('Payment details saved successfully');
        setEditDialogOpen(false);
        fetchTrainerPayments();
      }
    } catch (err) {
      console.error('Error saving payment details:', err);
      showToast(err.response?.data?.error || 'Failed to save payment details', 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  // Complete Withdrawal Request (Mark Completed + Enter UTR)
  const handleCompleteRequest = async () => {
    if (!activeRequest) return;
    try {
      setSubmittingAction(true);
      const res = await api.put(`/wallet/admin/withdrawal-requests/${activeRequest.id}`, {
        status: 'completed',
        reference_id: referenceId,
        admin_remarks: adminRemarks || 'Payout processed successfully',
      });
      if (res.data?.success) {
        showToast(`Request #${activeRequest.id} marked as completed`);
        setProcessDialogOpen(false);
        setActiveRequest(null);
        setReferenceId('');
        setAdminRemarks('');
        fetchWithdrawalRequests();
        fetchTrainerPayments();
      }
    } catch (err) {
      console.error('Error processing payout:', err);
      showToast(err.response?.data?.error || 'Failed to process payout', 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  // Reject Withdrawal Request (Refunds amount back to trainer's wallet)
  const handleRejectRequest = async () => {
    if (!activeRequest) return;
    try {
      setSubmittingAction(true);
      const res = await api.put(`/wallet/admin/withdrawal-requests/${activeRequest.id}`, {
        status: 'rejected',
        admin_remarks: adminRemarks || 'Withdrawal request rejected by admin',
      });
      if (res.data?.success) {
        showToast(`Request #${activeRequest.id} rejected. ₹${activeRequest.amount} refunded to trainer wallet.`);
        setRejectDialogOpen(false);
        setActiveRequest(null);
        setAdminRemarks('');
        fetchWithdrawalRequests();
        fetchTrainerPayments();
      }
    } catch (err) {
      console.error('Error rejecting request:', err);
      showToast(err.response?.data?.error || 'Failed to reject request', 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  // Table Columns for Tab 0: Trainer Payment Details
  const paymentColumns = [
    { id: 'trainer', label: 'Trainer Details' },
    { id: 'wallet_balance', label: 'Wallet Balance' },
    { id: 'payout_method', label: 'Payout Method' },
    { id: 'beneficiary', label: 'Account Holder' },
    { id: 'account_info', label: 'Bank / UPI Details' },
    { id: 'pending', label: 'Pending Requests' },
    { id: 'total_withdrawn', label: 'Total Paid Out' },
    { id: 'actions', label: 'Actions' },
  ];

  // Table Columns for Tab 1: Withdrawal Requests
  const requestColumns = [
    { id: 'id', label: 'Req ID' },
    { id: 'date', label: 'Date & Time' },
    { id: 'trainer', label: 'Trainer' },
    { id: 'amount', label: 'Amount' },
    { id: 'payout_details', label: 'Transfer To' },
    { id: 'status', label: 'Status' },
    { id: 'reference', label: 'UTR / Ref ID' },
    { id: 'actions', label: 'Actions' },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>
          Trainer Payments & Withdrawals
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Access and manage trainers' bank and UPI payout details, verify credentials, and process earnings withdrawals.
        </Typography>
      </Box>

      {/* Top Metric Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#e3f2fd', color: '#1976d2', mr: 2 }}>
                <PeopleIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Total Trainers</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.total_trainers || 0}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#e8f5e9', color: '#2e7d32', mr: 2 }}>
                <AccountBalanceIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Bank / UPI Added</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.configured_accounts || 0}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff3e0', color: '#ed6c02', mr: 2 }}>
                <HourglassEmptyIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Pending Payouts</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ed6c02' }}>
                  {summary.total_pending_requests || 0} (₹{parseFloat(summary.total_pending_amount || 0).toLocaleString('en-IN')})
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f3e5f5', color: '#7b1fa2', mr: 2 }}>
                <PriceCheckIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Total Completed</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                  ₹{parseFloat(summary.total_completed_amount || 0).toLocaleString('en-IN')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Tabs */}
      <Paper sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<AccountBalanceIcon />}
            iconPosition="start"
            label="Trainer Payment Details (Bank & UPI)"
            sx={{ fontWeight: 600, py: 2 }}
          />
          <Tab
            icon={<AccountBalanceWalletIcon />}
            iconPosition="start"
            label={`Withdrawal Requests ${summary.total_pending_requests ? `(${summary.total_pending_requests})` : ''}`}
            sx={{ fontWeight: 600, py: 2 }}
          />
        </Tabs>
      </Paper>

      {/* TAB 0: TRAINER PAYMENT DETAILS */}
      {activeTab === 0 && (
        <Box>
          {/* Filter Bar */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search Trainer Name, Phone, A/C, or UPI"
                  value={searchTrainer}
                  onChange={(e) => setSearchTrainer(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchTrainerPayments(); }}
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Payout Method</InputLabel>
                  <Select
                    value={filterMethod}
                    label="Payout Method"
                    onChange={(e) => setFilterMethod(e.target.value)}
                  >
                    <MenuItem value="all">All Methods</MenuItem>
                    <MenuItem value="bank">Bank Transfer Only</MenuItem>
                    <MenuItem value="upi">UPI Only</MenuItem>
                    <MenuItem value="not_configured">Payment Details Not Added</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={12} md={5} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  onClick={fetchTrainerPayments}
                  startIcon={<SearchIcon />}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => { setSearchTrainer(''); setFilterMethod('all'); }}
                  startIcon={<ClearIcon />}
                >
                  Reset
                </Button>
                <Button
                  variant="outlined"
                  onClick={fetchTrainerPayments}
                  startIcon={<RefreshIcon />}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Table */}
          <DataTable
            data={trainersData}
            columns={paymentColumns}
            loading={loading}
            emptyMessage="No trainer payment details found matching criteria"
            renderRow={(trainer) => {
              const isConfigured = !!trainer.payment_details_id;
              const isBank = trainer.payout_method === 'bank';
              const balance = parseFloat(trainer.wallet_balance || 0);

              return (
                <TableRow key={trainer.trainer_id} hover>
                  {/* Trainer Details */}
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {trainer.trainer_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: #{trainer.trainer_id} | {trainer.trainer_phone || 'No phone'}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      {trainer.trainer_email || ''}
                    </Typography>
                  </TableCell>

                  {/* Wallet Balance */}
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: balance > 0 ? '#2e7d32' : 'text.primary',
                      }}
                    >
                      ₹{balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Coins: {parseInt(trainer.coins || 0)}
                    </Typography>
                  </TableCell>

                  {/* Payout Method */}
                  <TableCell>
                    {!isConfigured ? (
                      <Chip label="Not Added" size="small" variant="outlined" color="default" />
                    ) : isBank ? (
                      <Chip
                        icon={<AccountBalanceIcon fontSize="small" />}
                        label="Bank Transfer"
                        size="small"
                        color="primary"
                        variant="filled"
                      />
                    ) : (
                      <Chip
                        icon={<QrCodeIcon fontSize="small" />}
                        label="UPI"
                        size="small"
                        color="secondary"
                        variant="filled"
                      />
                    )}
                  </TableCell>

                  {/* Beneficiary */}
                  <TableCell>
                    <Typography variant="body2">
                      {trainer.account_holder_name || '-'}
                    </Typography>
                  </TableCell>

                  {/* Bank / UPI Details */}
                  <TableCell>
                    {!isConfigured ? (
                      <Typography variant="caption" color="text.secondary">Details not submitted by trainer</Typography>
                    ) : isBank ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {trainer.bank_name || 'Bank'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            A/C: {trainer.account_number || '-'}
                          </Typography>
                          {trainer.account_number && (
                            <Tooltip title="Copy Account Number">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(trainer.account_number, 'Account Number')}
                              >
                                <ContentCopyIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            IFSC: {trainer.ifsc_code || '-'}
                          </Typography>
                          {trainer.ifsc_code && (
                            <Tooltip title="Copy IFSC">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(trainer.ifsc_code, 'IFSC Code')}
                              >
                                <ContentCopyIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1565c0' }}>
                          {trainer.upi_id || '-'}
                        </Typography>
                        {trainer.upi_id && (
                          <Tooltip title="Copy UPI ID">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(trainer.upi_id, 'UPI ID')}
                            >
                              <ContentCopyIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </TableCell>

                  {/* Pending Requests */}
                  <TableCell>
                    {trainer.pending_requests_count > 0 ? (
                      <Chip
                        label={`${trainer.pending_requests_count} Pending`}
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">0</Typography>
                    )}
                  </TableCell>

                  {/* Total Paid Out */}
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      ₹{parseFloat(trainer.total_withdrawn || 0).toLocaleString('en-IN')}
                    </Typography>
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <Tooltip title="Edit / View Details">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenEdit(trainer)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            }}
          />
        </Box>
      )}

      {/* TAB 1: WITHDRAWAL REQUESTS */}
      {activeTab === 1 && (
        <Box>
          {/* Filter Bar */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search Trainer Name, Phone, or Req #"
                  value={searchRequest}
                  onChange={(e) => setSearchRequest(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchWithdrawalRequests(); }}
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterRequestStatus}
                    label="Status"
                    onChange={(e) => setFilterRequestStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="pending">Pending Only</MenuItem>
                    <MenuItem value="completed">Completed / Paid</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                <Button
                  variant="contained"
                  onClick={fetchWithdrawalRequests}
                  startIcon={<SearchIcon />}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => { setSearchRequest(''); setFilterRequestStatus('all'); }}
                  startIcon={<ClearIcon />}
                >
                  Reset
                </Button>
                <Button
                  variant="outlined"
                  onClick={fetchWithdrawalRequests}
                  startIcon={<RefreshIcon />}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Table */}
          <DataTable
            data={withdrawalRequests}
            columns={requestColumns}
            loading={loading}
            emptyMessage="No withdrawal requests found"
            renderRow={(req) => {
              const isBank = req.payout_method === 'bank';
              const isPending = req.status === 'pending';
              const isCompleted = req.status === 'completed';
              const isRejected = req.status === 'rejected';

              return (
                <TableRow key={req.id} hover>
                  {/* Req ID */}
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      #{req.id}
                    </Typography>
                  </TableCell>

                  {/* Date */}
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(req.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(req.created_at).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </TableCell>

                  {/* Trainer */}
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {req.trainer_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {req.trainer_phone || ''}
                    </Typography>
                  </TableCell>

                  {/* Amount */}
                  <TableCell>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1a237e' }}>
                      ₹{parseFloat(req.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </Typography>
                  </TableCell>

                  {/* Transfer To */}
                  <TableCell>
                    {isBank ? (
                      <Box>
                        <Chip
                          icon={<AccountBalanceIcon fontSize="small" />}
                          label={req.bank_name || 'Bank Transfer'}
                          size="small"
                          color="primary"
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption" display="block">
                          Holder: {req.account_holder_name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            A/C: {req.account_number}
                          </Typography>
                          <IconButton size="small" onClick={() => copyToClipboard(req.account_number, 'A/C Number')}>
                            <ContentCopyIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          IFSC: {req.ifsc_code}
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Chip
                          icon={<QrCodeIcon fontSize="small" />}
                          label="UPI Transfer"
                          size="small"
                          color="secondary"
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption" display="block">
                          Holder: {req.account_holder_name || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#1565c0' }}>
                            {req.upi_id}
                          </Typography>
                          <IconButton size="small" onClick={() => copyToClipboard(req.upi_id, 'UPI ID')}>
                            <ContentCopyIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    {isPending && (
                      <Chip
                        icon={<HourglassEmptyIcon fontSize="small" />}
                        label="Pending"
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {isCompleted && (
                      <Chip
                        icon={<CheckCircleIcon fontSize="small" />}
                        label="Completed"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {isRejected && (
                      <Chip
                        icon={<CancelIcon fontSize="small" />}
                        label="Rejected"
                        color="error"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </TableCell>

                  {/* Reference */}
                  <TableCell>
                    {req.reference_id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {req.reference_id}
                        </Typography>
                        <IconButton size="small" onClick={() => copyToClipboard(req.reference_id, 'Reference / UTR')}>
                          <ContentCopyIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {isPending ? (
                        <>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => {
                              setActiveRequest(req);
                              setReferenceId('');
                              setAdminRemarks('');
                              setProcessDialogOpen(true);
                            }}
                          >
                            Pay
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => {
                              setActiveRequest(req);
                              setAdminRemarks('');
                              setRejectDialogOpen(true);
                            }}
                          >
                            Reject
                          </Button>
                        </>
                      ) : (
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => {
                            setActiveRequest(req);
                            setViewDetailsOpen(true);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            }}
          />
        </Box>
      )}

      {/* MODAL 1: EDIT / VIEW TRAINER PAYMENT DETAILS */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedTrainer ? `Payment Details: ${selectedTrainer.trainer_name}` : 'Trainer Payment Details'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTrainer && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Trainer ID: #{selectedTrainer.trainer_id} | Phone: {selectedTrainer.trainer_phone || '-'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mt: 0.5 }}>
                Current Wallet Balance: ₹{parseFloat(selectedTrainer.wallet_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          )}

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Payout Method</Typography>
            <RadioGroup
              row
              value={editFormData.payout_method}
              onChange={(e) => setEditFormData({ ...editFormData, payout_method: e.target.value })}
            >
              <FormControlLabel value="bank" control={<Radio />} label="Bank Account" />
              <FormControlLabel value="upi" control={<Radio />} label="UPI ID" />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            label="Beneficiary / Account Holder Name"
            value={editFormData.account_holder_name}
            onChange={(e) => setEditFormData({ ...editFormData, account_holder_name: e.target.value })}
            margin="normal"
            size="small"
          />

          {editFormData.payout_method === 'bank' ? (
            <>
              <TextField
                fullWidth
                label="Bank Name (e.g. HDFC Bank, SBI)"
                value={editFormData.bank_name}
                onChange={(e) => setEditFormData({ ...editFormData, bank_name: e.target.value })}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Bank Account Number"
                value={editFormData.account_number}
                onChange={(e) => setEditFormData({ ...editFormData, account_number: e.target.value })}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="IFSC Code (e.g. HDFC0001234)"
                value={editFormData.ifsc_code}
                onChange={(e) => setEditFormData({ ...editFormData, ifsc_code: e.target.value.toUpperCase() })}
                margin="normal"
                size="small"
              />
            </>
          ) : (
            <TextField
              fullWidth
              label="UPI ID / VPA (e.g. name@okhdfcbank)"
              value={editFormData.upi_id}
              onChange={(e) => setEditFormData({ ...editFormData, upi_id: e.target.value })}
              margin="normal"
              size="small"
              placeholder="e.g. 9876543210@paytm or trainer@okhdfcbank"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePaymentDetails}
            disabled={submittingAction}
          >
            {submittingAction ? <CircularProgress size={20} /> : 'Save Payment Details'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL 2: COMPLETE WITHDRAWAL (PROCESS PAYOUT) */}
      <Dialog
        open={processDialogOpen}
        onClose={() => setProcessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#2e7d32' }}>
          Mark Payout as Completed
        </DialogTitle>
        <DialogContent dividers>
          {activeRequest && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please transfer <strong>₹{parseFloat(activeRequest.amount).toLocaleString('en-IN')}</strong> to{' '}
                <strong>{activeRequest.trainer_name}</strong> via net banking / UPI, then enter the transaction UTR below.
              </Alert>

              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Payout Destination:
              </Typography>
              {activeRequest.payout_method === 'bank' ? (
                <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1.5, mt: 1 }}>
                  <Typography variant="body2"><strong>Holder:</strong> {activeRequest.account_holder_name}</Typography>
                  <Typography variant="body2"><strong>Bank:</strong> {activeRequest.bank_name}</Typography>
                  <Typography variant="body2"><strong>A/C Number:</strong> {activeRequest.account_number}</Typography>
                  <Typography variant="body2"><strong>IFSC:</strong> {activeRequest.ifsc_code}</Typography>
                </Box>
              ) : (
                <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1.5, mt: 1 }}>
                  <Typography variant="body2"><strong>Holder:</strong> {activeRequest.account_holder_name || '-'}</Typography>
                  <Typography variant="body2"><strong>UPI ID:</strong> {activeRequest.upi_id}</Typography>
                </Box>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            required
            label="Bank UTR / Transaction Reference Number"
            placeholder="e.g. UTR384918239012 or IMPS Reference"
            value={referenceId}
            onChange={(e) => setReferenceId(e.target.value)}
            margin="normal"
            size="small"
          />

          <TextField
            fullWidth
            label="Admin Notes / Remarks"
            value={adminRemarks}
            onChange={(e) => setAdminRemarks(e.target.value)}
            margin="normal"
            size="small"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCompleteRequest}
            disabled={!referenceId.trim() || submittingAction}
          >
            {submittingAction ? <CircularProgress size={20} /> : 'Confirm Payout Complete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL 3: REJECT WITHDRAWAL (REFUNDS MONEY) */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#d32f2f' }}>
          Reject Withdrawal Request #{activeRequest?.id}
        </DialogTitle>
        <DialogContent dividers>
          {activeRequest && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Rejecting this request will <strong>immediately refund ₹{parseFloat(activeRequest.amount).toLocaleString('en-IN')}</strong> back into{' '}
              <strong>{activeRequest.trainer_name}'s</strong> wallet balance.
            </Alert>
          )}

          <TextField
            fullWidth
            required
            label="Reason for Rejection"
            placeholder="e.g. Invalid account number, name mismatch, etc."
            value={adminRemarks}
            onChange={(e) => setAdminRemarks(e.target.value)}
            margin="normal"
            size="small"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectRequest}
            disabled={!adminRemarks.trim() || submittingAction}
          >
            {submittingAction ? <CircularProgress size={20} /> : 'Confirm Rejection & Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL 4: VIEW REQUEST DETAILS */}
      <Dialog
        open={viewDetailsOpen}
        onClose={() => setViewDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Withdrawal Request #{activeRequest?.id}
        </DialogTitle>
        <DialogContent dividers>
          {activeRequest && (
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
                Amount: ₹{parseFloat(activeRequest.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Status:</strong> {activeRequest.status.toUpperCase()}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Trainer:</strong> {activeRequest.trainer_name} ({activeRequest.trainer_phone})
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Requested On:</strong> {new Date(activeRequest.created_at).toLocaleString('en-IN')}
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Payout Destination ({activeRequest.payout_method === 'bank' ? 'Bank Account' : 'UPI'}):
              </Typography>
              {activeRequest.payout_method === 'bank' ? (
                <Box sx={{ pl: 1 }}>
                  <Typography variant="body2"><strong>Holder:</strong> {activeRequest.account_holder_name}</Typography>
                  <Typography variant="body2"><strong>Bank:</strong> {activeRequest.bank_name}</Typography>
                  <Typography variant="body2"><strong>Account Number:</strong> {activeRequest.account_number}</Typography>
                  <Typography variant="body2"><strong>IFSC:</strong> {activeRequest.ifsc_code}</Typography>
                </Box>
              ) : (
                <Box sx={{ pl: 1 }}>
                  <Typography variant="body2"><strong>Holder:</strong> {activeRequest.account_holder_name || '-'}</Typography>
                  <Typography variant="body2"><strong>UPI ID:</strong> {activeRequest.upi_id}</Typography>
                </Box>
              )}

              {activeRequest.reference_id && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2">
                    <strong>Transaction UTR / Reference:</strong> {activeRequest.reference_id}
                  </Typography>
                </>
              )}

              {activeRequest.admin_remarks && (
                <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                  <strong>Admin Remarks:</strong> {activeRequest.admin_remarks}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
